import { networkManager } from '../config/NetworkManager';
import { getLoaderControl, setLoaderControl as initializeLoaderControl } from '../config/loaderControl';

const withLoader = async (fn) => {
  const { showLoader, hideLoader } = getLoaderControl();
  try {
    showLoader();
    return await fn();
  } finally {
    hideLoader();
  }
};

const api = {
  auth: {
    login: async (credentials) => withLoader(async () => {
      const response = await fetch(`${networkManager.BASE_URL}/Authorization/LoginUser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Login failed');
      }

      return response.json();
    }),

    signup: async (payload) => withLoader(async () => {
      const response = await fetch(`${networkManager.BASE_URL}/Authorization/RegisterUser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data || data.status !== "Success") {
        throw new Error(data?.message || 'Signup failed');
      }

      return data;
    }),

    confirmemail: async (token) => {
      const response = await fetch(`${networkManager.BASE_URL}/Authorization/ConfirmEmail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const contentType = response.headers.get('content-type');
      const data = contentType?.includes('application/json')
        ? await response.json()
        : null;

      if (!response.ok || !data || data.status !== "Success") {
        throw new Error(data?.message || 'Email confirmation failed');
      }

      return data;
    },

    setpassword: async (token, password) => {
      const response = await fetch(`${networkManager.BASE_URL}/Authorization/SetPassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userToken: token,
          newPassword: password,
        }),
      });

      const data = await response.json();
      return {
        ok: response.ok,
        status: response.status,
        message: data?.message,
        data,
      };
    },

    resetpassword: async (token, password, confirmPassword, email) => {
      const response = await fetch(`${networkManager.BASE_URL}/Authorization/ResetPassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: password,
          token: token,
          confirmPassword: confirmPassword,
          email: email
        }),
      });

      const data = await response.json();
      return {
        ok: response.ok,
        status: response.status,
        message: data?.message,
        data,
      };
    },

    forgotPassword: async (email) => {
      const response = await fetch(`${networkManager.BASE_URL}/Authorization/ForgetPassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "An error occurred while sending the reset email.");
      }

      return data;
    },
  },

  // Oracle Forms Modernization Chat API
  chat: {
    sendQuery: async (query, asyncMode = false) => {
      // Don't use withLoader here as ChatInterface has its own loading state
      const payload = { query, use_local_recorder: true };
      try {
        let uid = localStorage.getItem('ibeam_recorder_user_id');
        if (!uid) {
          uid = `web-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
          localStorage.setItem('ibeam_recorder_user_id', uid);
        }
        payload.user_id = uid;
      } catch (_) {
        /* localStorage unavailable */
      }
      const response = await fetch(`${networkManager.EDS_BACKEND_URL}/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...payload,
          async_mode: asyncMode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Request failed with status ${response.status}`);
      }

      return response.json();
    },

    // Get async query task status
    getQueryTaskStatus: async (taskId) => {
      const response = await fetch(`${networkManager.EDS_BACKEND_URL}/query/status/${taskId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to get task status: ${response.status}`);
      }

      return response.json();
    },

    // Conversion Test Report APIs
    listReportTypes: async () => {
      const response = await fetch(`${networkManager.QA_AGENT_URL}/reports/types`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch report types: ${response.status}`);
      }

      return response.json();
    },

    listReports: async (reportType = null, formName = null) => {
      let url = `${networkManager.QA_AGENT_URL}/reports/list`;
      const params = new URLSearchParams();
      
      if (reportType) {
        params.append('report_type', reportType);
      }
      if (formName) {
        params.append('form_name', formName);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch reports: ${response.status}`);
      }

      return response.json();
    },

    getReportByTypeAndForm: async (reportType, formName, fileType = 'html') => {
      const url = `${networkManager.QA_AGENT_URL}/reports/${encodeURIComponent(reportType)}/${encodeURIComponent(formName)}?file_type=${fileType}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': fileType === 'html' ? 'text/html' : 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = fileType === 'html' 
          ? await response.text().catch(() => null)
          : await response.json().catch(() => null);
        throw new Error(errorData?.detail || errorData || `Failed to fetch report: ${response.status}`);
      }

      return fileType === 'html' ? response.text() : response.json();
    },

    getReportInfo: async (reportType, formName) => {
      const url = `${networkManager.QA_AGENT_URL}/reports/${encodeURIComponent(reportType)}/${encodeURIComponent(formName)}/info`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch report info: ${response.status}`);
      }

      return response.json();
    },

    // Legacy endpoints for backward compatibility
    getReport: async (reportName, category = null) => {
      const url = category
        ? `${networkManager.QA_AGENT_URL}/reports/legacy/${encodeURIComponent(reportName)}?category=${encodeURIComponent(category)}`
        : `${networkManager.QA_AGENT_URL}/reports/legacy/${encodeURIComponent(reportName)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/html',
        },
      });

      if (!response.ok) {
        const errorData = await response.text().catch(() => null);
        throw new Error(errorData || `Failed to fetch report: ${response.status}`);
      }

      return response.text();
    },

    // Conversion Checkpoints APIs
    listCheckpointForms: async () => {
      const response = await fetch(`${networkManager.CHECKPOINTS_URL}/checkpoints/forms`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch forms: ${response.status}`);
      }

      return response.json();
    },

    getFormCheckpoints: async (formName) => {
      const response = await fetch(`${networkManager.CHECKPOINTS_URL}/checkpoints/forms/${encodeURIComponent(formName)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch checkpoints: ${response.status}`);
      }

      return response.json();
    },

    getAllCheckpoints: async () => {
      const response = await fetch(`${networkManager.CHECKPOINTS_URL}/checkpoints/all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch all checkpoints: ${response.status}`);
      }

      return response.json();
    },

    getRecentCheckpoints: async (minutes = 5) => {
      const response = await fetch(`${networkManager.CHECKPOINTS_URL}/checkpoints/recent?minutes=${minutes}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch recent checkpoints: ${response.status}`);
      }

      return response.json();
    },

    listTestCases: async () => {
      const response = await fetch(`${networkManager.QA_AGENT_URL}/testcases/list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch test cases: ${response.status}`);
      }

      return response.json();
    },

    downloadTestCase: async (testcaseName) => {
      const response = await fetch(`${networkManager.QA_AGENT_URL}/testcases/${encodeURIComponent(testcaseName)}/download`, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to download test case: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = testcaseName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { status: 'success', message: `Test case '${testcaseName}' downloaded successfully` };
    },
  },

  artifacts: {
    getBackendContents: async (path = '') => {
      const params = path ? `path=${encodeURIComponent(path)}` : '';
      const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/backend/contents?${params}`);
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.message || `Failed to fetch artifacts (${response.status})`);
      }
      return response.json();
    },
    getFrontendContents: async (path = '') => {
      const params = path ? `path=${encodeURIComponent(path)}` : '';
      const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/frontend/contents?${params}`);
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.message || `Failed to fetch artifacts (${response.status})`);
      }
      return response.json();
    },
    getBackendFile: async (path) => {
      const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/backend/file?path=${encodeURIComponent(path)}`);
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.message || `Failed to fetch file (${response.status})`);
      }
      return response.json();
    },
    getFrontendFile: async (path) => {
      const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/frontend/file?path=${encodeURIComponent(path)}`);
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.message || `Failed to fetch file (${response.status})`);
      }
      return response.json();
    },
    getMultipleFrontendFiles: async (paths) => {
      const results = await Promise.allSettled(
        paths.map(p => api.artifacts.getFrontendFile(p))
      );
      const fileMap = {};
      results.forEach((result, idx) => {
        fileMap[paths[idx]] = result.status === 'fulfilled'
          ? (result.value.content ?? '')
          : '';
      });
      return fileMap;
    },
    editFile: async (content, prompt, filename, source) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 min for long LLM edits
      try {
        const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/edit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content, prompt, filename, source }),
          signal: controller.signal,
        });
        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          const msg = Array.isArray(errBody.detail) ? errBody.detail.map((e) => e?.msg || e).join('; ') : (errBody.detail || errBody.message);
          throw new Error(msg || `Edit failed (${response.status})`);
        }
        const data = await response.json();
        if (data == null || typeof data.content !== 'string') {
          throw new Error('Edit API returned invalid response (missing content)');
        }
        return data;
      } finally {
        clearTimeout(timeoutId);
      }
    },
    commitFile: async (path, content, source, commitMessage) => {
      const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/commit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path, content, source, commit_message: commitMessage }),
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.message || `Commit failed (${response.status})`);
      }
      return response.json();
    },

    commitMultipleFiles: async (changes, commitMessage) => {
      const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/commit-multiple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          changes: changes.map(c => ({
            path: c.path,
            content: c.content,
            source: c.source
          })),
          commit_message: commitMessage 
        }),
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.message || `Multi-file commit failed (${response.status})`);
      }
      return response.json();
    },

    // List all converted forms
    listForms: async () => {
      const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/list-forms`);
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.message || `Failed to list forms (${response.status})`);
      }
      return response.json();
    },

    // Get file tree for a specific form
    getFormFiles: async (formName) => {
      const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/form-files?form_name=${encodeURIComponent(formName)}`);
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.message || `Failed to get form files (${response.status})`);
      }
      return response.json();
    },

    // Get form screenshot (GIF/PNG) extracted from the form source ZIP
    getFormScreenshot: async (formName) => {
      const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/form-screenshot?form_name=${encodeURIComponent(formName)}`);
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.message || `Failed to get form screenshot (${response.status})`);
      }
      return response.json();
    },

    // Enhanced Feedback APIs - Knowledge Graph Integration
    processFeedback: async (formName, feedback, feedbackType = null, affectedFiles = [], currentContents = null) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 min timeout
      try {
        const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/feedback/process`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            form_name: formName,
            feedback,
            feedback_type: feedbackType,
            affected_files: affectedFiles,
            ...(currentContents && Object.keys(currentContents).length > 0
              ? { current_contents: currentContents }
              : {}),
          }),
          signal: controller.signal,
        });
        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.detail || `Feedback processing failed (${response.status})`);
        }
        return response.json();
      } finally {
        clearTimeout(timeoutId);
      }
    },

    analyzeFeedback: async (formName, feedback) => {
      const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/feedback/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form_name: formName, feedback }),
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || `Analysis failed (${response.status})`);
      }
      return response.json();
    },

    getFormContext: async (formName, feedback = '') => {
      const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/feedback/context`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form_name: formName, feedback }),
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || `Context fetch failed (${response.status})`);
      }
      return response.json();
    },

    editFileEnhanced: async (content, prompt, filename, source, formName = null, includeContext = false) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000);
      try {
        const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/edit-enhanced`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            content, 
            prompt, 
            filename, 
            source,
            form_name: formName,
            include_related_context: includeContext
          }),
          signal: controller.signal,
        });
        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.detail || `Edit failed (${response.status})`);
        }
        const data = await response.json();
        if (data == null || typeof data.content !== 'string') {
          throw new Error('Edit API returned invalid response (missing content)');
        }
        return data;
      } finally {
        clearTimeout(timeoutId);
      }
    },

    storeFeedback: async (formName, feedback, resolution, affectedFiles, category = 'general') => {
      const params = new URLSearchParams({
        form_name: formName,
        feedback,
        resolution,
        affected_files: affectedFiles.join(','),
        category
      });
      const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/feedback/store?${params}`, {
        method: 'POST',
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || `Store failed (${response.status})`);
      }
      return response.json();
    },

    // Coding Standards APIs
    searchCodingStandards: async (query, k = 10, category = null) => {
      const params = new URLSearchParams({ query, k: k.toString() });
      if (category) params.append('category', category);
      const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/standards/search?${params}`);
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || `Search failed (${response.status})`);
      }
      return response.json();
    },

    loadDefaultStandards: async () => {
      const response = await fetch(`${networkManager.ARTIFACTS_URL || networkManager.BASE_URL}/artifacts/standards/load-defaults`, {
        method: 'POST',
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || `Load failed (${response.status})`);
      }
      return response.json();
    },
  },

  // V2 Task-based Conversion APIs
  v2: {
    // Start conversion (returns task_id immediately)
    convertForm: async (conversionRequest) => {
      const response = await fetch(`${networkManager.EDS_BACKEND_URL}/v2/convert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(conversionRequest),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Conversion request failed: ${response.status}`);
      }

      return response.json();
    },

    // Get task status
    getTaskStatus: async (taskId) => {
      const response = await fetch(`${networkManager.EDS_BACKEND_URL}/v2/status/${taskId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to get task status: ${response.status}`);
      }

      return response.json();
    },

    // List all tasks
    listTasks: async (limit = 50) => {
      const response = await fetch(`${networkManager.EDS_BACKEND_URL}/v2/tasks?limit=${limit}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to list tasks: ${response.status}`);
      }

      return response.json();
    },

    // Delete a task
    deleteTask: async (taskId) => {
      const response = await fetch(`${networkManager.EDS_BACKEND_URL}/v2/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to delete task: ${response.status}`);
      }

      return response.json();
    },
  },
};

// Export the API and the init function
export default api;
export { initializeLoaderControl };